/**
 * @file ShopOverviewTab.tsx
 * @description Вкладка «Обзор» карточки магазина (phase19.28 этап А;
 * uiux-platform-shops §4; PL8, PL9). На телефоне — «Требует внимания» и
 * под ним «Сведения»; от 1280 — две колонки 2/3 и 1/3.
 *
 * Выручки и заказов нет (В4): только то, что мешает открыть сайт, и
 * служебные сведения. Строки внимания — реестр `shopAttentionItems`.
 * Под сведениями — «Собрать стартовый сайт ещё раз» (решение 2026-10-24);
 * первой строкой внимания — «Договор с платформой» с «Отметить» (PL9, PL18).
 */

import { useShopCardContext } from "../../../hooks/platform/useShopCard";
import { pluralRu } from "../../../lib/plural";
import { shopAttentionItems } from "../../../lib/platform/shop-attention";
import { formatPlatformDate } from "../../../lib/platform/shop-format";
import { ownerLabel, primaryHostLabel } from "../../../lib/platform/shop-list-filter";
import { timeZoneOffsetLabel } from "../../../lib/shop-time";
import { ShopContractRow } from "../../../components/platform/shops/ShopContractRow";
import { SiteStarterButton } from "../../../components/platform/shops/SiteStarterButton";
import { AttentionList } from "../../../components/ui/AttentionList";
import { DocumentMetaGrid, type DocumentMetaItem } from "../../../components/ui/DocumentMetaGrid";
import { SectionCard } from "../../../components/ui/SectionCard";

/** «Анна (приглашена)» — пока владелец не принял приглашение. */
function ownerValue(name: string, status: "none" | "pending" | "accepted"): string {
  return status === "pending" ? `${name} (приглашён)` : name;
}

/** Смещение пояса; имя пояса, которого не знает браузер, — как есть. */
function zoneLabel(zone: string): string {
  try {
    return `${timeZoneOffsetLabel(zone)} · ${zone}`;
  } catch {
    return zone;
  }
}

export function ShopOverviewTab() {
  const { shop, base, reload } = useShopCardContext();
  const attention = shopAttentionItems(shop, base);
  const meta: DocumentMetaItem[] = [
    { label: "Главный адрес", value: <span className="break-all">{primaryHostLabel(shop, "нет")}</span> },
    { label: "Владелец", value: ownerValue(ownerLabel(shop), shop.ownerInvitation.status) },
    { label: "Часовой пояс", value: zoneLabel(shop.timeZone) },
    { label: "Создан", value: formatPlatformDate(shop.createdAt), mono: true },
    { label: "Адресов", value: `${shop.domainsCount} ${pluralRu(shop.domainsCount, ["адрес", "адреса", "адресов"])}` },
  ];
  if (shop.contactEmail) meta.push({ label: "Почта магазина", value: <span className="break-all">{shop.contactEmail}</span> });

  return (
    <div className="grid min-w-0 gap-4 xl:grid-cols-3">
      <SectionCard title="Требует внимания" className="min-w-0 xl:col-span-2">
        <ShopContractRow shop={shop} onChanged={reload} />
        <AttentionList
          aria-label="Что мешает открыть сайт"
          items={attention}
          emptyText="Всё готово к открытию сайта"
        />
      </SectionCard>
      <SectionCard title="Сведения" className="min-w-0">
        <div className="flex flex-col gap-3">
          <DocumentMetaGrid items={meta} className="lg:grid-cols-2 xl:grid-cols-1" />
          <SiteStarterButton shopId={shop.id} shopName={shop.displayName} onApplied={(result) => (result.applied ? reload() : undefined)} />
        </div>
      </SectionCard>
    </div>
  );
}
